import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TrendingUp, Star, Heart, Check, Film, Tv, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getImageUrl, addMovie, getTrendingExtended, getUserMovies, updateMovie } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const MediaCard = ({ item, userMovies, onQuickAction }) => {
  const title = item.title || item.name;
  const poster = getImageUrl(item.poster_path, 'w342');
  const isMovie = item.media_type === 'movie' || item.title;
  
  const userEntry = userMovies?.find(m => m.tmdb_id === item.id);
  const isInWatchlist = userEntry?.status === 'watchlist';
  const isWatched = userEntry?.status === 'watched';
  
  const handleWatchlistClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isMovie) return;
    onQuickAction(item, 'watchlist');
  };

  const handleWatchedClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isMovie) return;
    onQuickAction(item, 'watched');
  };
  
  return (
    <Link 
      to={`/${isMovie ? 'movie' : 'tv'}/${item.id}`}
      className="group relative aspect-[2/3] rounded-lg overflow-hidden bg-gray-900 transition-all duration-300 hover:ring-2 hover:ring-indigo-500 hover:scale-[1.02]"
    >
      {poster ? (
        <img src={poster} alt={title} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-800">
          {isMovie ? <Film className="w-12 h-12 text-gray-600" /> : <Tv className="w-12 h-12 text-gray-600" />}
        </div>
      )}
      
      {/* Quick Action Buttons */}
      {isMovie && (
        <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {!isWatched && (
            <button
              onClick={handleWatchlistClick}
              className={`w-9 h-9 rounded-full backdrop-blur-md flex items-center justify-center transition-all ${
                isInWatchlist ? 'bg-pink-500/80 text-white' : 'bg-black/50 text-white/70 hover:bg-black/70 hover:text-pink-400'
              }`}
            >
              <Heart className={`w-4 h-4 ${isInWatchlist ? 'fill-current' : ''}`} />
            </button>
          )}
          <button
            onClick={handleWatchedClick}
            className={`w-9 h-9 rounded-full backdrop-blur-md flex items-center justify-center transition-all ${
              isWatched ? 'bg-green-500/80 text-white' : 'bg-black/50 text-white/70 hover:bg-black/70 hover:text-green-400'
            }`}
          >
            <Check className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Status Badge */}
      {isWatched && (
        <div className="absolute top-2 left-2 bg-green-500 rounded-full p-1.5">
          <Check className="w-3 h-3 text-white" />
        </div>
      )}
      {isInWatchlist && !isWatched && (
        <div className="absolute top-2 left-2 bg-pink-500 rounded-full p-1.5">
          <Heart className="w-3 h-3 text-white fill-current" />
        </div>
      )}
      
      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-white font-semibold text-sm mb-1 line-clamp-2">{title}</h3>
          <div className="flex items-center gap-2 text-xs text-gray-300">
            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
            <span>{item.vote_average?.toFixed(1)}</span>
            <span>•</span>
            <span>{(item.release_date || item.first_air_date)?.slice(0, 4)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export const DiscoverPage = () => {
  const { type } = useParams(); // 'movie' or 'tv'
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [userMovies, setUserMovies] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const mediaType = type || 'movie';
  const isMovie = mediaType === 'movie';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getTrendingExtended(mediaType, page);
        setItems(data.results || []);
        setTotalPages(Math.min(data.total_pages || 1, 20)); // Limit to 20 pages

        if (isAuthenticated) {
          const moviesResponse = await getUserMovies();
          setUserMovies(moviesResponse.movies || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, mediaType, isAuthenticated]);

  const handleQuickAction = async (item, action) => {
    if (!isAuthenticated) {
      toast.error('Log in om films toe te voegen');
      return;
    }

    const existingEntry = userMovies.find(m => m.tmdb_id === item.id);

    try {
      if (action === 'watchlist') {
        if (existingEntry) {
          toast.info('Film staat al in je bibliotheek');
          return;
        }
        await addMovie({
          tmdb_id: item.id,
          title: item.title,
          poster_path: item.poster_path,
          backdrop_path: item.backdrop_path,
          status: 'watchlist',
          runtime: item.runtime || 0,
          genres: []
        });
        toast.success('Toegevoegd aan watchlist');
      } else if (action === 'watched') {
        if (existingEntry) {
          await updateMovie(existingEntry.id, { status: 'watched' });
        } else {
          await addMovie({
            tmdb_id: item.id,
            title: item.title,
            poster_path: item.poster_path,
            backdrop_path: item.backdrop_path,
            status: 'watched',
            runtime: item.runtime || 0,
            genres: []
          });
        }
        toast.success('Gemarkeerd als gekeken');
      }
      
      // Refresh user movies
      const moviesResponse = await getUserMovies();
      setUserMovies(moviesResponse.movies || []);
    } catch (error) {
      toast.error('Kon actie niet uitvoeren');
    }
  };

  return (
    <div data-testid="discover-page" className="min-h-screen p-6 md:p-12">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <TrendingUp className="w-6 h-6 text-indigo-400" />
          <h1 className="text-3xl md:text-4xl font-display font-bold">
            Trending {isMovie ? 'Films' : 'Series'}
          </h1>
        </div>
        <p className="text-gray-400">Ontdek de populairste {isMovie ? 'films' : 'series'} van dit moment</p>
      </div>

      {/* Type Toggle */}
      <div className="flex gap-2 mb-8">
        <Link to="/discover/movie">
          <Button 
            variant={isMovie ? 'default' : 'secondary'}
            className={isMovie ? 'bg-indigo-600' : 'bg-white/5'}
          >
            <Film className="w-4 h-4 mr-2" />
            Films
          </Button>
        </Link>
        <Link to="/discover/tv">
          <Button 
            variant={!isMovie ? 'default' : 'secondary'}
            className={!isMovie ? 'bg-indigo-600' : 'bg-white/5'}
          >
            <Tv className="w-4 h-4 mr-2" />
            Series
          </Button>
        </Link>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mb-8">
            {items.map((item) => (
              <MediaCard 
                key={item.id} 
                item={{ ...item, media_type: mediaType }}
                userMovies={userMovies}
                onQuickAction={handleQuickAction}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="secondary"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="bg-white/5"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Vorige
            </Button>
            <span className="text-gray-400">
              Pagina {page} van {totalPages}
            </span>
            <Button
              variant="secondary"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="bg-white/5"
            >
              Volgende
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default DiscoverPage;
